// Google Analytics, loaded from the page.
//
// **`G-HR753TK64P` is a GA4 measurement id, not a Tag Manager container.** A
// GTM container is `GTM-XXXXXXX` and loads `gtm.js`; a `G-` id is what
// `gtag.js` takes, and the two are different products with different snippets.
// This is the gtag one, because it is the one that id works with — swapping to
// a container is this file and a different constant, if a `GTM-` id turns up.
//
// **Not in `index.html`, which is where the snippet usually goes.** Two reasons,
// and the second is the one that matters:
//
//   - A tag in the HTML fires on every dev server and every Playwright browser.
//     The e2e suite alone opens a dozen of those a run, so the property would
//     fill with sessions from a machine nobody is playing on — and the first
//     thing anybody asks of an analytics property is "how many people played",
//     which those make unanswerable.
//   - Loaded from here it is *code*: it can be gated, it can be tested without
//     a browser, and the decision about when it runs is written down beside the
//     reasons rather than being implicit in which file it sits in.
//
// It is deliberately the whole of what this does. No custom events, no
// screen-view calls on the menu's doors: the app is one page and changes screen
// by state rather than by the History API, so GA4's own enhanced measurement
// sees one page load and that is honest. Inventing events nobody asked for
// would be inventing a schema nobody has agreed.

/** The property. One place, so a second copy cannot disagree with it. */
export const MEASUREMENT_ID = "G-HR753TK64P";

/**
 * How often a run reports that it is still being played.
 *
 * **Plies, not moves, and the difference is worth stating** because a run has
 * more than two armies in it: a camp takes a turn and an ally takes a turn,
 * so ten plies is not ten of the player's moves.
 * It is a *pace* signal either way — what it answers is "do people get past
 * the first ten minutes", and any consistent unit answers that.
 */
export const RUN_EVERY = 10;

/**
 * Which milestone a run is at, or 0 for none yet.
 *
 * Pure, and a number rather than a boolean, so the caller can latch on it: a
 * run's ply count goes *backwards* when the player undoes, and "is this a
 * multiple of ten" would fire again on the way back up. Comparing milestones
 * means a step back and forward over the same boundary reports once.
 */
export const milestoneOf = (plies: number): number => Math.floor(plies / RUN_EVERY);

const SRC = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;

/**
 * Should this page report at all?
 *
 * Production builds only. `import.meta.env.DEV` is the dev server and
 * `VITE_E2E` is the built bundle the smokes drive — the same pair
 * `DEV_TOOLS` uses, for the same reason: those are the two ways this app runs
 * on a machine where nobody is playing it.
 *
 * Taken as an argument rather than read here so the decision is testable, and
 * so a caller that wants it off has somewhere to say so.
 */
export function shouldReport(env: {
  DEV?: boolean;
  VITE_E2E?: unknown;
} = import.meta.env): boolean {
  return !env.DEV && !env.VITE_E2E;
}

/**
 * Put the tag on the page.
 *
 * The ordinary gtag bootstrap: `dataLayer` and the `gtag` shim exist before the
 * script arrives, so the two calls below are queued rather than lost, and the
 * script itself is `async` — nothing about the board waits for it.
 *
 * Idempotent. `index.tsx` calls it once, but React 19's StrictMode mounts twice
 * in development and a second `<script>` for the same property would double
 * every page view if this were ever called from a component.
 */
export function startAnalytics(
  doc: Document = document,
  env: Parameters<typeof shouldReport>[0] = import.meta.env
): void {
  if (!shouldReport(env)) return;
  if (doc.querySelector(`script[src="${SRC}"]`)) return;

  const window_ = doc.defaultView as (Window & { dataLayer?: unknown[] }) | null;
  if (!window_) return;
  const layer = (window_.dataLayer = window_.dataLayer ?? []);
  // **`arguments`, not a rest parameter, and that is not a stylistic hangover.**
  // The tag reads the pushed object as an `Arguments`; an array pushed in its
  // place is a different shape and the call is ignored. This is Google's own
  // shim and one of the few places where copying a vendor's code exactly is the
  // correct thing to do — hence the `_` names, which exist only to say how many
  // arguments the two calls below pass.
  function gtag(_command?: unknown, _value?: unknown) {
    // eslint-disable-next-line prefer-rest-params
    layer.push(arguments);
  }
  gtag("js", new Date());
  gtag("config", MEASUREMENT_ID);

  const script = doc.createElement("script");
  script.async = true;
  script.src = SRC;
  doc.head.appendChild(script);
}

/**
 * One event.
 *
 * **Counts and shapes, never content.** Every parameter passed anywhere in this
 * app is a number or a fixed word from a list this repo controls — how far in,
 * which mode, which seat. Nothing a player typed, nothing another player owns.
 * The room code in particular is *not* an event parameter: it belongs to
 * whoever you are playing as much as to you, and the consent screen says so in
 * as many words.
 *
 * A no-op when the tag never loaded, so every call site can be written without
 * asking whether analytics is on — the same shape `setHeartbeat` gives the
 * tuner. `dataLayer` is what gtag reads, and pushing to it before the script
 * arrives is how the vendor's own snippet works, so an event fired in the first
 * second of a page load is queued rather than lost.
 */
export function track(
  event: string,
  params: Record<string, string | number | boolean> = {},
  view: (Window & { dataLayer?: unknown[] }) | null = typeof window === "undefined" ? null : window
): void {
  const layer = view?.dataLayer;
  if (!layer) return;
  // The shape gtag expects for an event, pushed the way the shim pushes it.
  layer.push(["event", event, params]);
}
